import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { fetchTier3Map, fetchTier3Alerts } from "../lib/api";
import { isSoundOn, playChime } from "../lib/alphaSound";

export const TIER3_STYLES = {
  "Strong Confirmation": { cls: "bg-neon/15 border-neon/50 text-neon", short: "T3 STRONG" },
  "Good Confirmation": { cls: "bg-[#F0C000]/15 border-[#F0C000]/50 text-[#F0C000]", short: "T3 GOOD" },
  "Early Signs": { cls: "bg-[#2AABEE]/10 border-[#2AABEE]/40 text-[#2AABEE]", short: "T3 EARLY" },
};

export const useTier3Map = () => {
  const q = useQuery({ queryKey: ["tier3Map"], queryFn: fetchTier3Map, refetchInterval: 120000, staleTime: 60000 });
  return q.data || {};
};

// Small chip for coin rows/detail: shows when a coin has Tier 3 confirmation (Early Signs+).
export const Tier3Badge = ({ address, symbol, compact = false }) => {
  const map = useTier3Map();
  const t = map[address];
  if (!t || !TIER3_STYLES[t.label]) return null;
  const s = TIER3_STYLES[t.label];
  const title = `Tier 3 Confirmation ${t.score}/100 — ${t.label}\n${(t.reasons || []).map((r) => `• ${r}`).join("\n")}`;
  return (
    <span
      data-testid={`tier3-badge-${address}`}
      title={title}
      className={`text-[9px] font-mono px-1 py-0.5 border tracking-wide whitespace-nowrap ${s.cls}`}
    >
      🎯 {compact ? s.short : `${s.short} ${t.score}`}
    </span>
  );
};

// Global watcher: chime + toast anywhere in the app when a coin hits Strong Confirmation.
export const Tier3Watcher = () => {
  const nav = useNavigate();
  const q = useQuery({ queryKey: ["tier3Alerts"], queryFn: fetchTier3Alerts, refetchInterval: 60000, staleTime: 45000 });
  const seen = useRef(Number(localStorage.getItem("t3_toast_ts") || 0));

  useEffect(() => {
    if (!seen.current) {
      seen.current = Math.floor(Date.now() / 1000);
      localStorage.setItem("t3_toast_ts", String(seen.current));
    }
    const fresh = (q.data?.events || []).filter((e) => (e.alertedAt || 0) > seen.current);
    if (!fresh.length) return;
    seen.current = Math.max(...fresh.map((e) => e.alertedAt));
    localStorage.setItem("t3_toast_ts", String(seen.current));
    if (isSoundOn()) playChime();
    const e = fresh[0];
    const extra = fresh.length > 1 ? ` (+${fresh.length - 1} more)` : "";
    toast.success(`🎯 Strong Confirmation: $${(e.symbol || e.mint.slice(0, 4)).toUpperCase()} scored ${e.score}/100${extra}`, {
      id: "tier3-alert",
      duration: 12000,
      action: { label: "View", onClick: () => nav("/confirmation") },
    });
  }, [q.data, nav]);

  return null;
};
